import React from 'react';
import { MapPin } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

/**
 * US states for the billing-address `state` dropdown. Two-letter ISO codes are
 * what NMI's `state` field expects on the Payment API (`NY`, `CA`, etc.) — full
 * names work too but vary in normalization, so we send the abbreviation.
 *
 * Order: alphabetical by full name. Add US territories at the bottom; NMI's AVS
 * runs across them transparently.
 */
export const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' },
  { code: 'AZ', name: 'Arizona' }, { code: 'AR', name: 'Arkansas' },
  { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' },
  { code: 'DC', name: 'District of Columbia' }, { code: 'FL', name: 'Florida' },
  { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' }, { code: 'IL', name: 'Illinois' },
  { code: 'IN', name: 'Indiana' }, { code: 'IA', name: 'Iowa' },
  { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' }, { code: 'ME', name: 'Maine' },
  { code: 'MD', name: 'Maryland' }, { code: 'MA', name: 'Massachusetts' },
  { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' }, { code: 'MO', name: 'Missouri' },
  { code: 'MT', name: 'Montana' }, { code: 'NE', name: 'Nebraska' },
  { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' }, { code: 'NM', name: 'New Mexico' },
  { code: 'NY', name: 'New York' }, { code: 'NC', name: 'North Carolina' },
  { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' }, { code: 'OR', name: 'Oregon' },
  { code: 'PA', name: 'Pennsylvania' }, { code: 'RI', name: 'Rhode Island' },
  { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' }, { code: 'TX', name: 'Texas' },
  { code: 'UT', name: 'Utah' }, { code: 'VT', name: 'Vermont' },
  { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' }, { code: 'WI', name: 'Wisconsin' },
  { code: 'WY', name: 'Wyoming' },
  { code: 'PR', name: 'Puerto Rico' }, { code: 'GU', name: 'Guam' },
  { code: 'VI', name: 'U.S. Virgin Islands' }, { code: 'AS', name: 'American Samoa' },
  { code: 'MP', name: 'Northern Mariana Islands' },
];

/** US ZIP code: either 5 digits ("90210") or 5+4 ("90210-1234"). */
export const ZIP_REGEX = /^\d{5}(?:-?\d{4})?$/;

/**
 * Initial empty state shape — kept as a single object so callers can spread it
 * into their useState seed and consume the same shape elsewhere.
 */
export const EMPTY_BILLING_ADDRESS = {
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
};

/**
 * Validate a billing address. Returns an `errors` map (empty when valid). We
 * deliberately keep zip validation US-focused since NMI's AVS scoring assumes
 * a US address — international cards work without AVS, but most of Defera's
 * customers are US-based and we'd rather catch typos here than at the gateway.
 */
export function validateBillingAddress(values) {
  const errors = {};
  if (!values?.address1 || !values.address1.trim()) {
    errors.address1 = 'Street address is required';
  }
  if (!values?.city || !values.city.trim()) {
    errors.city = 'City is required';
  }
  if (!values?.state) {
    errors.state = 'State is required';
  }
  if (!values?.zip || !values.zip.trim()) {
    errors.zip = 'ZIP code is required';
  } else if (!ZIP_REGEX.test(values.zip.trim())) {
    errors.zip = 'Use a 5-digit ZIP (or ZIP+4)';
  }
  return errors;
}

const FIELD_BASE_CLASS =
  'w-full h-11 px-3.5 rounded-xl bg-white/[0.04] border border-white/[0.12] text-white text-[15px] font-normal placeholder:text-white/40 focus:outline-none focus:border-white/30 transition-colors';

const FIELD_ERROR_CLASS = 'border-red-400/60 focus:border-red-400/80';

/**
 * Shared billing-address form section. Used by both the NMI checkout form
 * (initial deposit) and the NMI restart-plan form (re-tokenizes a new card
 * for an existing booking). Centralising it here keeps validation rules,
 * field order, and visual styling in one place — important because NMI's
 * AVS scoring will rescore any of these flows.
 */
export default function BillingAddressFields({
  values,
  onChange,
  errors = {},
  disabled = false,
  idPrefix = 'billing',
}) {
  const set = (key) => (e) => {
    const value = typeof e === 'string' ? e : e?.target?.value;
    onChange({ ...values, [key]: value });
  };

  return (
    <div className="mt-8">
      <div className="flex items-center gap-2 mb-4">
        <MapPin className="w-5 h-5 text-white/70" />
        <h3 className="text-white text-sm font-normal tracking-wide uppercase">Billing address</h3>
      </div>

      <div className="space-y-3">
        <div>
          <label htmlFor={`${idPrefix}-address1`} className="text-xs text-white/60 font-normal mb-1.5 block">
            Address line 1
          </label>
          <input
            id={`${idPrefix}-address1`}
            type="text"
            inputMode="text"
            autoComplete="address-line1"
            disabled={disabled}
            value={values.address1}
            onChange={set('address1')}
            placeholder="Street address"
            className={`${FIELD_BASE_CLASS} ${errors.address1 ? FIELD_ERROR_CLASS : ''}`}
          />
          {errors.address1 && (
            <p className="text-[11px] text-red-300/90 mt-1.5">{errors.address1}</p>
          )}
        </div>

        <div>
          <label htmlFor={`${idPrefix}-address2`} className="text-xs text-white/60 font-normal mb-1.5 block">
            Address line 2 <span className="text-white/40">(optional)</span>
          </label>
          <input
            id={`${idPrefix}-address2`}
            type="text"
            inputMode="text"
            autoComplete="address-line2"
            disabled={disabled}
            value={values.address2}
            onChange={set('address2')}
            placeholder="Apt, suite, unit, etc."
            className={FIELD_BASE_CLASS}
          />
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="col-span-2 sm:col-span-2">
            <label htmlFor={`${idPrefix}-city`} className="text-xs text-white/60 font-normal mb-1.5 block">
              City
            </label>
            <input
              id={`${idPrefix}-city`}
              type="text"
              autoComplete="address-level2"
              disabled={disabled}
              value={values.city}
              onChange={set('city')}
              placeholder="City"
              className={`${FIELD_BASE_CLASS} ${errors.city ? FIELD_ERROR_CLASS : ''}`}
            />
            {errors.city && (
              <p className="text-[11px] text-red-300/90 mt-1.5">{errors.city}</p>
            )}
          </div>

          <div>
            <label htmlFor={`${idPrefix}-state`} className="text-xs text-white/60 font-normal mb-1.5 block">
              State
            </label>
            <Select
              value={values.state}
              onValueChange={set('state')}
              disabled={disabled}
            >
              <SelectTrigger
                id={`${idPrefix}-state`}
                className={`${FIELD_BASE_CLASS} ${errors.state ? FIELD_ERROR_CLASS : ''} justify-between`}
              >
                <SelectValue placeholder="State" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-white/10 text-white max-h-64">
                {US_STATES.map((s) => (
                  <SelectItem key={s.code} value={s.code} className="text-white">
                    {s.code} — {s.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.state && (
              <p className="text-[11px] text-red-300/90 mt-1.5">{errors.state}</p>
            )}
          </div>

          <div>
            <label htmlFor={`${idPrefix}-zip`} className="text-xs text-white/60 font-normal mb-1.5 block">
              ZIP
            </label>
            <input
              id={`${idPrefix}-zip`}
              type="text"
              inputMode="numeric"
              autoComplete="postal-code"
              disabled={disabled}
              value={values.zip}
              onChange={(e) => {
                // Allow digits and optional single hyphen for ZIP+4. Strip
                // everything else as the customer types so we don't have to
                // reject valid-looking input on submit.
                const cleaned = e.target.value.replace(/[^\d-]/g, '').slice(0, 10);
                onChange({ ...values, zip: cleaned });
              }}
              placeholder="ZIP code"
              maxLength={10}
              className={`${FIELD_BASE_CLASS} ${errors.zip ? FIELD_ERROR_CLASS : ''}`}
            />
            {errors.zip && (
              <p className="text-[11px] text-red-300/90 mt-1.5">{errors.zip}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
